/**
 * @fileoverview TaskNotesFeed — thin container wiring the shared
 * `NotesFeedView` to the **task** notes endpoints (`api.tasks.notes.*`).
 *
 * Mirrors `ContactNotesFeed` so the task side panel gets the same
 * chronological multi-note journal (post / edit / delete, author-only
 * editing, newest-first) as the job-contact detail panel. The AI summary
 * surface is hidden for tasks (`showSummary={false}`).
 *
 * Used by: src/calendar/key-date-side-panel.tsx
 */

import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { NotesFeedView } from "@/components/contacts/notes/NotesFeedView";
import type { FeedNote } from "@/components/contacts/notes/NoteRow";

interface TaskNotesFeedProps {
  keyDateId: Id<"jobKeyDates">;
  /** Read-only when the caller has no write access. */
  readOnly?: boolean;
  /** The current authenticated user (decides which rows are editable). */
  currentUserId?: Id<"users">;
}

export function TaskNotesFeed({
  keyDateId,
  readOnly = false,
  currentUserId,
}: TaskNotesFeedProps) {
  const { data: notes, isLoading } = useQuery({
    ...convexQuery(api.tasks.notes.listTaskNotes, { keyDateId }),
  });

  const createNote = useConvexMutation(api.tasks.notes.createTaskNote);
  const updateNote = useConvexMutation(api.tasks.notes.updateTaskNote);
  const deleteNote = useConvexMutation(api.tasks.notes.deleteTaskNote);

  const handlePost = useCallback(
    async (content: string) => {
      await createNote({ keyDateId, content });
    },
    [keyDateId, createNote],
  );

  const handleSaveNote = useCallback(
    async (noteId: string, content: string) => {
      await updateNote({ noteId: noteId as Id<"jobKeyDateNotes">, content });
    },
    [updateNote],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      await deleteNote({ noteId: noteId as Id<"jobKeyDateNotes"> });
    },
    [deleteNote],
  );

  const notesList: Array<FeedNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  return (
    <NotesFeedView
      notes={notesList}
      notesCount={notesList.length}
      isLoading={isLoading}
      readOnly={readOnly}
      currentUserId={currentUserId}
      showSummary={false}
      onPost={handlePost}
      onSaveNote={handleSaveNote}
      onDeleteNote={handleDeleteNote}
    />
  );
}
